import {
  eachDayOfInterval,
  endOfMonth,
  format,
  formatISO,
  parseISO,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";

export const PERIOD_PRESETS = {
  LAST_WEEK: "last_week",
  LAST_MONTH: "last_month",
  MONTH_TO_DATE: "month_to_date",
  LAST_SERVICE_MONTH: "last_service_month",
  SINCE_DAY: "since_day",
  CUSTOM: "custom",
};

/** Períodos do total de combustível na planilha de abastecimentos. */
export const FUEL_TOTAL_PERIOD_PRESETS = {
  LAST_DAY: "last_day",
  LAST_WEEK: "last_week",
  LAST_MONTH: "last_month",
  LAST_YEAR: "last_year",
  CUSTOM: "custom",
};

const FUEL_TOTAL_PERIOD_STORAGE_PREFIX = "field-app-fuel-total-period-";

export const DEFAULT_FUEL_TOTAL_CHIP_ORDER = [
  FUEL_TOTAL_PERIOD_PRESETS.LAST_DAY,
  FUEL_TOTAL_PERIOD_PRESETS.LAST_WEEK,
  FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH,
  FUEL_TOTAL_PERIOD_PRESETS.LAST_YEAR,
  FUEL_TOTAL_PERIOD_PRESETS.CUSTOM,
];

export const DETAIL_SCOPE = {
  INTERVAL: "interval",
  SINCE_DAY: "since_day",
  WEEK: "week",
  MONTH: "month",
  CUSTOM: "custom",
};

const SINCE_DAY_STORAGE_PREFIX = "field-app-since-start-day-";

export function readStoredSinceStartDay(farmId, fallbackDay) {
  if (!farmId) return fallbackDay || todayIso();
  try {
    const stored = localStorage.getItem(`${SINCE_DAY_STORAGE_PREFIX}${farmId}`);
    if (stored && /^\d{4}-\d{2}-\d{2}$/.test(stored)) {
      return stored;
    }
  } catch (error) {
    // ignore
  }
  return (fallbackDay || todayIso()).slice(0, 10);
}

export function writeStoredSinceStartDay(farmId, dayKey) {
  if (!farmId || !dayKey) return;
  try {
    localStorage.setItem(`${SINCE_DAY_STORAGE_PREFIX}${farmId}`, dayKey.slice(0, 10));
  } catch (error) {
    // ignore
  }
}

export function dayKeyFromIso(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export function buildSinceDayPeriod(startDayKey) {
  const fromDay = dayKeyFromIso(startDayKey);
  if (!fromDay) {
    return buildLastWeekPeriod();
  }
  return {
    dateFrom: `${fromDay}T00:00:00`,
    dateTo: endBoundForDay(todayIso()),
    periodPreset: PERIOD_PRESETS.SINCE_DAY,
  };
}

export function todayIso() {
  return format(new Date(), "yyyy-MM-dd");
}

/** Dia padrão do gráfico de rastreamento: hoje, ou o último dia do período. */
export function defaultTrackingChartDayKey(periodDayKeys) {
  const today = todayIso();
  if (periodDayKeys && periodDayKeys.indexOf(today) !== -1) {
    return today;
  }
  if (periodDayKeys && periodDayKeys.length) {
    return periodDayKeys[periodDayKeys.length - 1];
  }
  return today;
}

export function defaultTrackingChartDayKeys(periodDayKeys) {
  const dayKey = defaultTrackingChartDayKey(periodDayKeys);
  return dayKey ? [dayKey] : [];
}

export function nowLocalIso() {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
}

function endBoundForDay(dayKey) {
  if (dayKey === todayIso()) {
    // Com fuso explícito — evita cortar pontos da tarde quando o Mac
    // está em UTC-4 e a API interpreta o relógio como horário do Brasil.
    return formatISO(new Date());
  }
  return `${dayKey}T23:59:59`;
}

export function selectionForDayKey(selection, dayKey) {
  if (!selection || !dayKey) return selection;
  return {
    ...selection,
    dateFrom: `${dayKey}T00:00:00`,
    dateTo: endBoundForDay(dayKey),
  };
}

export function dayKeysNewestFirst(dateFrom, dateTo) {
  return dayKeysForPeriod(dateFrom, dateTo).slice().reverse();
}

export function buildLastWeekPeriod() {
  const end = new Date();
  const start = subDays(end, 6);
  const endDay = format(end, "yyyy-MM-dd");
  return {
    dateFrom: `${format(start, "yyyy-MM-dd")}T00:00:00`,
    dateTo: endBoundForDay(endDay),
    periodPreset: PERIOD_PRESETS.LAST_WEEK,
  };
}

export function buildLastMonthPeriod() {
  const end = new Date();
  const start = subDays(end, 29);
  const endDay = format(end, "yyyy-MM-dd");
  return {
    dateFrom: `${format(start, "yyyy-MM-dd")}T00:00:00`,
    dateTo: endBoundForDay(endDay),
    periodPreset: PERIOD_PRESETS.LAST_MONTH,
  };
}

export function buildLastDayPeriod() {
  const endDay = format(new Date(), "yyyy-MM-dd");
  return {
    dateFrom: `${endDay}T00:00:00`,
    dateTo: endBoundForDay(endDay),
    periodPreset: FUEL_TOTAL_PERIOD_PRESETS.LAST_DAY,
  };
}

export function buildLastYearPeriod() {
  const end = new Date();
  const start = subDays(end, 364);
  const endDay = format(end, "yyyy-MM-dd");
  return {
    dateFrom: `${format(start, "yyyy-MM-dd")}T00:00:00`,
    dateTo: endBoundForDay(endDay),
    periodPreset: FUEL_TOTAL_PERIOD_PRESETS.LAST_YEAR,
  };
}

export function resolveFuelTotalPeriod(preset, customFrom, customTo) {
  switch (preset) {
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_DAY:
      return buildLastDayPeriod();
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_WEEK:
      return buildLastWeekPeriod();
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_YEAR:
      return buildLastYearPeriod();
    case FUEL_TOTAL_PERIOD_PRESETS.CUSTOM:
      return buildCustomPeriod(customFrom, customTo);
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH:
    default:
      return buildLastMonthPeriod();
  }
}

export function fuelTotalPeriodLabel(preset, t) {
  switch (preset) {
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_DAY:
      return t("Último dia");
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_WEEK:
      return t("Última semana");
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH:
      return t("Último mês");
    case FUEL_TOTAL_PERIOD_PRESETS.LAST_YEAR:
      return t("Último ano");
    default:
      return t("Personalizado");
  }
}

export function normalizeFuelTotalChipOrder(order) {
  const allowed = new Set(DEFAULT_FUEL_TOTAL_CHIP_ORDER);
  const seen = new Set();
  const normalized = [];
  (order || []).forEach((id) => {
    if (allowed.has(id) && !seen.has(id)) {
      normalized.push(id);
      seen.add(id);
    }
  });
  DEFAULT_FUEL_TOTAL_CHIP_ORDER.forEach((id) => {
    if (!seen.has(id)) {
      normalized.push(id);
    }
  });
  return normalized;
}

export function readStoredFuelTotalPeriod(farmId) {
  if (!farmId) return null;
  try {
    const raw = localStorage.getItem(`${FUEL_TOTAL_PERIOD_STORAGE_PREFIX}${farmId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      customFrom: parsed.customFrom || "",
      customTo: parsed.customTo || "",
      chipOrder: normalizeFuelTotalChipOrder(parsed.chipOrder),
      activeTablePreset: parsed.activeTablePreset || FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH,
    };
  } catch (error) {
    return null;
  }
}

export function writeStoredFuelTotalPeriod(farmId, { customFrom, customTo, chipOrder, activeTablePreset }) {
  if (!farmId) return;
  try {
    const current = readStoredFuelTotalPeriod(farmId) || {};
    localStorage.setItem(
      `${FUEL_TOTAL_PERIOD_STORAGE_PREFIX}${farmId}`,
      JSON.stringify({
        customFrom:
          customFrom !== undefined ? customFrom : current.customFrom || "",
        customTo: customTo !== undefined ? customTo : current.customTo || "",
        chipOrder: normalizeFuelTotalChipOrder(
          chipOrder !== undefined ? chipOrder : current.chipOrder
        ),
        activeTablePreset:
          activeTablePreset !== undefined
            ? activeTablePreset
            : current.activeTablePreset || FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH,
      })
    );
  } catch (error) {
    // ignore
  }
}

export function buildMonthToDatePeriod() {
  const end = new Date();
  const start = startOfMonth(end);
  const endDay = format(end, "yyyy-MM-dd");
  return {
    dateFrom: `${format(start, "yyyy-MM-dd")}T00:00:00`,
    dateTo: endBoundForDay(endDay),
    periodPreset: PERIOD_PRESETS.MONTH_TO_DATE,
  };
}

export function buildServiceMonthPeriod(lastActivityIso) {
  if (!lastActivityIso) {
    return buildLastWeekPeriod();
  }
  let activityDate;
  try {
    activityDate = parseISO(String(lastActivityIso));
  } catch (error) {
    return buildLastWeekPeriod();
  }
  const start = startOfMonth(activityDate);
  const end = endOfMonth(activityDate);
  const endDay = format(end, "yyyy-MM-dd");
  const today = todayIso();
  const cappedEndDay = endDay > today ? today : endDay;
  return {
    dateFrom: `${format(start, "yyyy-MM-dd")}T00:00:00`,
    dateTo: endBoundForDay(cappedEndDay),
    periodPreset: PERIOD_PRESETS.LAST_SERVICE_MONTH,
  };
}

export function buildCustomPeriod(dateFromDay, dateToDay) {
  const fromDay = String(dateFromDay || "").slice(0, 10);
  const toDay = String(dateToDay || fromDay).slice(0, 10);
  if (!fromDay || !toDay || fromDay > toDay) {
    return buildLastWeekPeriod();
  }
  return {
    dateFrom: `${fromDay}T00:00:00`,
    dateTo: endBoundForDay(toDay),
    periodPreset: PERIOD_PRESETS.CUSTOM,
  };
}

export function buildPeriodDayButtons(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return [];

  let start;
  let end;
  try {
    start = startOfDay(parseISO(dateFrom));
    end = startOfDay(parseISO(dateTo));
  } catch (error) {
    return [];
  }

  if (end < start) return [];

  return eachDayOfInterval({ start, end }).map((day) => {
    const key = format(day, "yyyy-MM-dd");
    return {
      key,
      label: format(day, "dd/MM/yyyy"),
      shortLabel: format(day, "dd/MM"),
      isToday: key === todayIso(),
    };
  });
}

/** Dia mais recente do período — visão inicial do rastreamento. */
export function defaultVisibleDayKeys(periodDayKeys) {
  if (!periodDayKeys || !periodDayKeys.length) return [];
  return [periodDayKeys[periodDayKeys.length - 1]];
}

export function dayKeysForPeriod(dateFrom, dateTo) {
  return buildPeriodDayButtons(dateFrom, dateTo).map((day) => day.key);
}

export function allPeriodDaysSelected(periodDayKeys, selectedDayKeys) {
  if (!periodDayKeys.length) return true;
  if (!selectedDayKeys || !selectedDayKeys.length) return false;
  return periodDayKeys.every((key) => selectedDayKeys.indexOf(key) !== -1);
}

export function selectionTimeBoundsMs(selection) {
  if (!selection || !selection.dateFrom || !selection.dateTo) {
    return { startMs: null, endMs: null };
  }
  const startMs = new Date(selection.dateFrom).getTime();
  const endMs = new Date(selection.dateTo).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return { startMs: null, endMs: null };
  }
  return { startMs, endMs };
}

export function resolveEffectiveSelection(selection, selectedDayKeys) {
  if (!selection) return null;

  const periodDayKeys = dayKeysForPeriod(selection.dateFrom, selection.dateTo);
  if (allPeriodDaysSelected(periodDayKeys, selectedDayKeys)) {
    return selection;
  }

  if (!selectedDayKeys || !selectedDayKeys.length) {
    return selection;
  }

  const sorted = selectedDayKeys.slice().sort();
  const fromDay = sorted[0];
  const toDay = sorted[sorted.length - 1];

  return {
    ...selection,
    dateFrom: `${fromDay}T00:00:00`,
    dateTo: endBoundForDay(toDay),
    periodAutoToday: sorted.length === 1 && fromDay === todayIso(),
  };
}

export function graphIndicesForTimeRange(graphData, startTimeMs, endTimeMs) {
  if (!graphData || !graphData.length || startTimeMs == null || endTimeMs == null) {
    return { startIndex: 0, endIndex: Math.max((graphData || []).length - 1, 0) };
  }

  let startIndex = 0;
  let endIndex = graphData.length - 1;

  for (let index = 0; index < graphData.length; index += 1) {
    if (graphData[index].time >= startTimeMs) {
      startIndex = index;
      break;
    }
  }

  for (let index = graphData.length - 1; index >= 0; index -= 1) {
    if (graphData[index].time <= endTimeMs) {
      endIndex = index;
      break;
    }
  }

  if (endIndex < startIndex) {
    endIndex = startIndex;
  }

  return { startIndex, endIndex };
}

export function formatSelectedDaysLabel(selectedDayKeys) {
  if (!selectedDayKeys || !selectedDayKeys.length) {
    return "";
  }
  if (selectedDayKeys.length === 1) {
    const [year, month, day] = String(selectedDayKeys[0]).slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }
  const sorted = selectedDayKeys.slice().sort();
  const formatDay = (value) => {
    const [year, month, day] = String(value).slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  };
  return `${formatDay(sorted[0])} — ${formatDay(sorted[sorted.length - 1])}`;
}

export function refreshLivePeriodEnd(selection) {
  if (!selection) return selection;
  const endDay = String(selection.dateTo || "").slice(0, 10);
  if (endDay !== todayIso()) {
    return selection;
  }
  const nextEnd = endBoundForDay(endDay);
  if (nextEnd === selection.dateTo) {
    return selection;
  }
  return {
    ...selection,
    dateTo: nextEnd,
    periodAutoToday: Boolean(selection.periodAutoToday),
  };
}

export function periodEndsToday(selection) {
  if (!selection || !selection.dateTo) return false;
  return String(selection.dateTo).slice(0, 10) === todayIso();
}

export function presetLabel(preset, t) {
  switch (preset) {
    case PERIOD_PRESETS.LAST_WEEK:
      return t("Última semana");
    case PERIOD_PRESETS.LAST_MONTH:
      return t("Último mês");
    case PERIOD_PRESETS.MONTH_TO_DATE:
      return t("Desde o 1º dia do mês");
    case PERIOD_PRESETS.LAST_SERVICE_MONTH:
      return t("Último mês de serviço");
    default:
      return t("Período personalizado");
  }
}

/** Rastreamento aceita um operador por vez — mantém só o primeiro se houver vários. */
export function trimSelectionToSingleOperator(selection) {
  if (!selection || !Array.isArray(selection.operatorIds)) {
    return selection;
  }
  if (selection.operatorIds.length <= 1) {
    return selection;
  }
  const operatorId = Number(selection.operatorIds[0]);
  const names = selection.operatorNames || [];
  const index = selection.operatorIds.findIndex(
    (id) => Number(id) === operatorId
  );
  const operatorName = index >= 0 && names[index] ? names[index] : names[0] || "";
  return {
    ...selection,
    operatorIds: [operatorId],
    operatorNames: operatorName ? [operatorName] : [],
  };
}

export function buildSingleOperatorSelection(selection, operatorId) {
  if (!selection || operatorId == null) {
    return selection;
  }
  const id = Number(operatorId);
  const ids = selection.operatorIds || [];
  const names = selection.operatorNames || [];
  const index = ids.findIndex((entry) => Number(entry) === id);
  const operatorName =
    index >= 0 && names[index] != null ? names[index] : names[0] || "";
  return {
    ...selection,
    operatorIds: [id],
    operatorNames: operatorName ? [operatorName] : [],
  };
}
